import { create } from 'zustand';
import {
  cancelPendingChange,
  cancelSubscription,
  changeAddon,
  changePlan,
  createCheckout,
  fetchCreditBalance,
  fetchSubscription,
  getOverageSettings,
  openCustomerPortal,
  updateOverageSettings,
  validateProviderKey,
} from '../utils/billingApi';
import useProjectStore from '../components/ProjectCanvas/useProjectStore';

/**
 * Billing v3 self-serve store (plan §PR C3). Checkout/portal actions end with a
 * full-page redirect — Stripe.js is never loaded in the app.
 */
const useBillingStore = create((set, get) => ({
  subscription: null,
  balance: null,
  overageSettings: null,
  loaded: false,
  loading: false,
  overageLoading: false,
  error: null,

  fetchSubscription: async () => {
    const subscription = await fetchSubscription();
    if (subscription) {
      set({ subscription });
      useProjectStore.setState({
        billingPlan: subscription.plan,
        billingFreeTierState: subscription.freeTier?.state || null,
      });
    }
    return subscription;
  },

  fetchBalance: async () => {
    const balance = await fetchCreditBalance();
    if (balance) {
      set({ balance });
      useProjectStore.setState({ creditBalance: balance.includedBalance });
    }
    return balance;
  },

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      await Promise.all([get().fetchSubscription(), get().fetchBalance()]);
      set({ loaded: true });
    } catch (e) {
      set({ error: 'Unable to load billing information.' });
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Stripe changes (checkout, interval switch) only land in our DB once the
   * customer.subscription.* webhook processes a few seconds later. Poll fetchSubscription until
   * the predicate holds so the UI updates without a manual reload. Each fetchSubscription also
   * syncs useProjectStore.billingPlan, releasing the onboarding gate.
   */
  awaitSubscriptionSettled: async (predicate, { attempts = 10, intervalMs = 2000 } = {}) => {
    for (let i = 0; i < attempts; i += 1) {
      const subscription = await get().fetchSubscription();
      if (predicate(subscription)) {
        await get().fetchBalance();
        return subscription;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return get().subscription;
  },

  awaitCheckoutSettled: async (opts) =>
    get().awaitSubscriptionSettled((s) => s?.plan && s.plan !== 'FREE', opts),

  startCheckout: async ({ interval, addonQuantity = 0 }) => {
    const result = await createCheckout({ plan: 'STANDARD', interval, addonQuantity });
    if (!result?.checkoutUrl) {
      throw new Error('No checkout URL returned');
    }
    window.location.assign(result.checkoutUrl);
  },

  changePlan: async (body) => {
    await changePlan(body);
    // billingInterval syncs via the customer.subscription.updated webhook a few seconds after the
    // API call, so poll until it reflects the requested interval instead of showing stale pricing.
    await get().awaitSubscriptionSettled(
      (s) => !body?.interval || s?.billingInterval === body.interval,
    );
  },

  changeAddon: async (quantity) => {
    await changeAddon({ quantity });
    await get().refresh();
  },

  cancelSubscription: async () => {
    await cancelSubscription();
    await get().refresh();
  },

  cancelPendingChange: async (id) => {
    await cancelPendingChange(id);
    await get().refresh();
  },

  openCustomerPortal: async () => {
    const result = await openCustomerPortal();
    if (!result?.url) {
      throw new Error('No portal URL returned');
    }
    window.location.assign(result.url);
  },

  fetchOverageSettings: async () => {
    set({ overageLoading: true });
    try {
      const overageSettings = await getOverageSettings();
      if (overageSettings) {
        set({ overageSettings });
      }
      return overageSettings;
    } finally {
      set({ overageLoading: false });
    }
  },

  updateOverageSettings: async (body) => {
    const overageSettings = await updateOverageSettings(body);
    if (overageSettings) {
      set({ overageSettings });
    }
    await get().fetchSubscription();
    return overageSettings;
  },

  validateProviderKey: async ({ credentialId }) => {
    const result = await validateProviderKey({ credentialId });
    if (result?.unlocked) {
      useProjectStore.setState({ billingFreeTierState: 'UNLOCKED' });
      await get().refresh();
    } else if (result && result.unlocked === false) {
      const err = new Error(result.message || 'Invalid API key');
      err.response = { data: result, status: 400 };
      throw err;
    }
    return result;
  },
}));

export default useBillingStore;
